import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import AuthedImage from "@/components/AuthedImage";
import { ArrowLeft, MessageCircle, X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, RotateCcw, Filter } from "lucide-react";
import { TransformWrapper, TransformComponent, useControls } from "react-zoom-pan-pinch";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { shareCardOnWhatsApp, shareSeriesOnWhatsApp } from "@/lib/whatsapp";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { useSavedDesigns } from "@/context/SavedDesignsContext";
import { useActivityTracker } from "@/hooks/useActivityTracker";
import { Heart } from "lucide-react";
import { usePriceVisibility, formatPriceForTrader } from "@/hooks/usePriceVisibility";

const formatINRStatic = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

// Small zoom-control overlay rendered inside the TransformWrapper context
function LightboxZoomControls() {
  const { zoomIn, zoomOut, resetTransform } = useControls();
  return (
    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1 bg-white/10 backdrop-blur-sm rounded-full px-1.5 py-1" data-testid="lightbox-zoom-controls">
      <button onClick={() => zoomOut()} type="button" className="p-2 text-white/85 hover:text-white" aria-label="Zoom out" data-testid="lightbox-zoom-out"><ZoomOut size={18} /></button>
      <button onClick={() => resetTransform()} type="button" className="p-2 text-white/85 hover:text-white" aria-label="Reset zoom" data-testid="lightbox-zoom-reset"><RotateCcw size={18} /></button>
      <button onClick={() => zoomIn()} type="button" className="p-2 text-white/85 hover:text-white" aria-label="Zoom in" data-testid="lightbox-zoom-in"><ZoomIn size={18} /></button>
    </div>
  );
}

export default function SeriesDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  // Presentation-mode toggle — trader can hide all prices to show their
  // device to a retail customer without exposing wholesale prices.
  const { visible: pricesVisible } = usePriceVisibility();
  const formatINR = (n) => (user && user.role !== "admin" ? formatPriceForTrader(n, pricesVisible) : formatINRStatic(n));
  // Religion filter propagated from the catalog. When present, the cards endpoint
  // returns only designs tagged with this audience (or "Common").
  const [searchParams, setSearchParams] = useSearchParams();
  const religion = searchParams.get("religion") || "";
  const [series, setSeries] = useState(null);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const track = useActivityTracker();

  // Report VIEW_CARD when the lightbox opens or the active design changes.
  // Backend de-duplicates per-card within the 2-minute window so flipping
  // back-and-forth through the same card won't inflate counts.
  useEffect(() => {
    if (lightboxIdx === null) return;
    const c = cards[lightboxIdx];
    if (c) track("VIEW_CARD", { card_id: c.id, series_id: id });
  }, [lightboxIdx, cards, id, track]);

  // Track every unauth-but-gate-verified visitor's series view on the backend
  // so the admin "Verified Visitors" tab shows which leads looked at what.
  // Logged-in users already have their activity captured via the richer
  // `useActivityTracker` pipeline, so we skip them here to avoid double counts.
  useEffect(() => {
    if (user) return;
    let visitorId = null;
    try { visitorId = localStorage.getItem("sv_gate_visitor_id"); } catch { /* private mode */ }
    if (!visitorId || !id) return;
    // Fire-and-forget. Failures are silent — we never want a tracking call to
    // surface as a user-visible error.
    api.post("/gate/track-view", { visitor_id: visitorId, series_id: id }).catch(() => {});
  }, [id, user]);

  // Series detail load — differentiates between a real 404 ("Series not found") and a
  // network/server hiccup. On a network hiccup we DON'T flash a misleading toast and
  // we transparently retry once after 3s so a transient backend blip doesn't kick the
  // user out of a series detail page they were trying to view.
  useEffect(() => {
    let active = true;
    let retryTimer = null;
    setLoading(true);
    const fetchOnce = () => Promise.all([
      api.get(`/series/${id}`),
      api.get(`/series/${id}/cards`, { params: religion ? { religion } : {} }),
    ]);
    fetchOnce()
      .then(([s, c]) => {
        if (!active) return;
        setSeries(s.data);
        setCards(c.data);
      })
      .catch((err) => {
        if (!active) return;
        const status = err?.response?.status;
        if (status === 404) {
          // Genuine missing series — surface to the user.
          toast.error("Series not found");
        } else {
          // Network / 5xx / timeout — retry once silently after 3s.
          retryTimer = setTimeout(() => {
            if (!active) return;
            fetchOnce()
              .then(([s, c]) => {
                if (!active) return;
                setSeries(s.data);
                setCards(c.data);
              })
              .catch((err2) => {
                if (!active) return;
                const s2 = err2?.response?.status;
                toast.error(s2 === 404 ? "Series not found" : "Couldn't load this series — please retry");
              })
              .finally(() => active && setLoading(false));
          }, 3000);
        }
      })
      .finally(() => {
        // If a retry was scheduled, defer the loading=false until it resolves.
        if (active && !retryTimer) setLoading(false);
      });
    return () => {
      active = false;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [id, religion]);

  const clearReligionFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("religion");
    setSearchParams(next, { replace: true });
  };

  const enquireSeries = async () => {
    const res = await shareSeriesOnWhatsApp(series, cards, user);
    if (!res.ok) toast.error(res.error || "Could not open WhatsApp");
  };

  const enquireCard = async (card) => {
    const cardForShare = {
      id: card.id,
      name: card.name,
      code: card.code,
      price: series.price,
      available: card.available,
      category: series.category,
    };
    const res = await shareCardOnWhatsApp(cardForShare, user);
    if (!res.ok) toast.error(res.error || "Could not open WhatsApp");
  };

  const closeLightbox = useCallback(() => setLightboxIdx(null), []);
  const goPrev = useCallback(() => setLightboxIdx((i) => (i === null ? null : (i - 1 + cards.length) % cards.length)), [cards.length]);
  const goNext = useCallback(() => setLightboxIdx((i) => (i === null ? null : (i + 1) % cards.length)), [cards.length]);

  // Keyboard navigation in lightbox
  useEffect(() => {
    if (lightboxIdx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightboxIdx, closeLightbox, goPrev, goNext]);

  if (loading) return <div className="p-16 text-center font-serif text-2xl text-[#D62828]">Loading…</div>;
  if (!series) return <div className="p-16 text-center text-[#4A4A4A]">Series not found.</div>;

  const activeCard = lightboxIdx !== null ? cards[lightboxIdx] : null;

  return (
    <div className="bg-[#FAF9F6]">
      <div className="max-w-7xl mx-auto px-4 md:px-12 lg:px-16 py-8">
        <button onClick={() => nav("/catalog")} className="inline-flex items-center gap-2 text-[#4A4A4A] hover:text-[#D62828] mb-8" data-testid="back-to-catalog">
          <ArrowLeft size={16} />
          <span className="tracking-luxe text-xs">Back to Catalog</span>
        </button>

        {/* Compact title strip — name, code, price, single-line enquire CTA.
            Replaces the old hero image; the designs grid below is now the immediate
            visual focus so customers can scan the actual cards instantly without
            scrolling past a duplicate sample image. */}
        <div className="mb-6" data-testid="series-header-compact">
          <div className="tracking-luxe text-[10px] text-[#F4A100] mb-2">{series.category || "Wedding Invitation Series"}</div>
          <h1 className="font-serif text-3xl md:text-4xl text-[#D62828] leading-tight break-words" data-testid="series-title">
            {series.name}
          </h1>
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mt-2">
            {series.code && <div className="tracking-luxe text-xs text-[#4A4A4A]">Series Code · {series.code}</div>}
            <div className="font-sans text-2xl text-[#D62828]" data-testid="series-detail-price">{formatINR(series.price)}</div>
            <span className="tracking-luxe text-[10px] text-[#4A4A4A]">per piece, across the series</span>
          </div>
        </div>

        {/* Designs grid — top-of-page now, per user request "as soon as I open the series
            directly show the other models added in the series and below those images show
            the details added like size, work etc." */}
        <h2 className="font-serif text-2xl md:text-3xl text-[#D62828] mb-2">Designs in this series</h2>
        <p className="text-[#4A4A4A] mb-4 text-sm">Tap any design to see it in big size.</p>

        {/* Active religion filter chip — visible when arriving here from a catalog filter. */}
        {religion && (
          <div
            className="bg-[#F5F2EA] border border-[#F4A100] inline-flex items-center gap-3 px-4 py-2 mb-6"
            data-testid="series-religion-filter"
          >
            <Filter size={13} className="text-[#D62828]" />
            <span className="text-xs text-[#1A1A1A]">
              Showing designs suitable for <span className="font-semibold">{religion}</span>
              {religion !== "Common" && <span className="text-[#4A4A4A]"> (and Common)</span>}
            </span>
            <button
              type="button"
              onClick={clearReligionFilter}
              data-testid="series-religion-filter-clear"
              className="text-[#4A4A4A] hover:text-[#D62828] tracking-luxe text-[10px] inline-flex items-center gap-1 underline underline-offset-2"
            >
              <X size={11} /> Show all designs
            </button>
          </div>
        )}

        {cards.length === 0 ? (
          <div className="bg-white border border-[#E5E0D8] py-16 text-center" data-testid="series-cards-empty">
            <div className="font-serif text-2xl text-[#D62828] mb-2">
              {religion ? `No ${religion} designs in this series yet` : "No designs added yet"}
            </div>
            <p className="text-[#4A4A4A]">
              {religion ? (
                <>This series has designs for other audiences. <button type="button" onClick={clearReligionFilter} className="underline underline-offset-2 text-[#D62828]">Show all designs</button>.</>
              ) : (
                "The atelier will add designs to this series shortly."
              )}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {cards.map((c, idx) => (
              <DesignRevealTile
                key={c.id}
                card={c}
                series={series}
                index={idx}
                seriesPrice={series.price}
                formatINR={formatINR}
                onOpenLightbox={() => setLightboxIdx(idx)}
                onEnquire={() => enquireCard(c)}
              />
            ))}
          </div>
        )}

        {/* Details + enquire CTA — moved below the designs grid per user feedback so the
            customer scans the actual designs first, then sees specs and the WhatsApp CTA. */}
        <div className="mt-12 sv-anim-in" data-testid="series-details-below-grid">
          {series.description && series.description.trim() && (
            <div
              className="bg-white border-l-2 border-[#F4A100] pl-4 py-2 mb-6 text-[#4A4A4A] leading-relaxed whitespace-pre-wrap max-w-3xl"
              data-testid="series-description"
            >
              {series.description}
            </div>
          )}

          {(series.paper || series.size || series.finish) && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6 max-w-3xl" data-testid="series-material-details">
              {series.paper && (
                <div className="bg-white border border-[#E5E0D8] px-4 py-3">
                  <div className="tracking-luxe text-[10px] text-[#F4A100] mb-1">Paper / Quality</div>
                  <div className="text-sm text-[#1A1A1A]">{series.paper}</div>
                </div>
              )}
              {series.size && (
                <div className="bg-white border border-[#E5E0D8] px-4 py-3">
                  <div className="tracking-luxe text-[10px] text-[#F4A100] mb-1">Size</div>
                  <div className="text-sm text-[#1A1A1A]">{series.size}</div>
                </div>
              )}
              {series.finish && (
                <div className="bg-white border border-[#E5E0D8] px-4 py-3">
                  <div className="tracking-luxe text-[10px] text-[#F4A100] mb-1">Finish / Work</div>
                  <div className="text-sm text-[#1A1A1A]">{series.finish}</div>
                </div>
              )}
            </div>
          )}

          <p className="text-[#4A4A4A] leading-relaxed mb-6 max-w-prose text-sm">
            This series has <span className="text-[#D62828] font-medium">{cards.length} design{cards.length === 1 ? "" : "s"}</span>. All cards share the same price.
          </p>

          <button
            onClick={enquireSeries}
            data-testid="enquire-series-whatsapp"
            className="inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#1ebe57] text-white px-7 py-3.5 transition-colors"
          >
            <MessageCircle size={16} />
            <span className="tracking-luxe text-xs">Enquire About Whole Series</span>
          </button>
        </div>
      </div>

      {/* Lightbox modal */}
      {activeCard && (
        <div
          className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex flex-col"
          onClick={closeLightbox}
          data-testid="card-lightbox"
          role="dialog"
        >
          {/* Top bar */}
          <div className="flex items-center justify-between px-4 md:px-8 py-3 text-white" onClick={(e) => e.stopPropagation()}>
            <div className="min-w-0">
              <div className="tracking-luxe text-[10px] text-[#F4A100]">{series.name}</div>
              <div className="font-serif text-lg md:text-2xl truncate" data-testid="lightbox-card-code">
                {activeCard.code || `Design ${lightboxIdx + 1}`}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="hidden sm:inline tracking-luxe text-[10px] text-white/70">{lightboxIdx + 1} / {cards.length}</span>
              {activeCard.available ? (
                <span className="bg-[#ECFDF5] text-[#065F46] tracking-luxe text-[10px] px-2.5 py-0.5 rounded-full">In Stock</span>
              ) : (
                <span className="bg-[#FEF2F2] text-[#991B1B] tracking-luxe text-[10px] px-2.5 py-0.5 rounded-full">Sold Out</span>
              )}
              <button
                onClick={closeLightbox}
                data-testid="lightbox-close"
                className="p-2 text-white/80 hover:text-white"
                aria-label="Close"
              >
                <X size={22} />
              </button>
            </div>
          </div>

          {/* Image area with pinch-to-zoom / double-tap / mouse-wheel */}
          <div className="flex-1 flex items-center justify-center relative overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {cards.length > 1 && (
              <button
                onClick={goPrev}
                data-testid="lightbox-prev"
                className="absolute left-1 md:left-4 top-1/2 -translate-y-1/2 z-20 p-2 md:p-3 bg-white/10 hover:bg-white/20 text-white rounded-full"
                aria-label="Previous design"
              >
                <ChevronLeft size={24} />
              </button>
            )}
            <TransformWrapper
              key={activeCard.id}
              initialScale={1}
              minScale={1}
              maxScale={3}
              centerOnInit
              doubleClick={{ mode: "toggle", step: 1.5 }}
              wheel={{ step: 0.2 }}
              pinch={{ step: 5 }}
              panning={{ velocityDisabled: true }}
              limitToBounds
            >
              <>
                <TransformComponent
                  wrapperStyle={{ width: "100%", height: "100%" }}
                  contentStyle={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}
                >
                  <AuthedImage
                    path={activeCard.image_path}
                    alt={activeCard.name}
                    className="max-h-[70vh] md:max-h-[78vh] max-w-full object-contain select-none"
                    draggable={false}
                    onContextMenu={(e) => e.preventDefault()}
                    style={{ touchAction: "none", WebkitUserSelect: "none", WebkitTouchCallout: "none", pointerEvents: "auto" }}
                    trackContext={{ cardId: activeCard.id, seriesId: series.id }}
                  />
                </TransformComponent>
                <LightboxZoomControls />
              </>
            </TransformWrapper>
            {cards.length > 1 && (
              <button
                onClick={goNext}
                data-testid="lightbox-next"
                className="absolute right-1 md:right-4 top-1/2 -translate-y-1/2 z-20 p-2 md:p-3 bg-white/10 hover:bg-white/20 text-white rounded-full"
                aria-label="Next design"
              >
                <ChevronRight size={24} />
              </button>
            )}
          </div>

          {/* Bottom action bar */}
          <div className="px-4 md:px-8 py-4 flex flex-wrap items-center justify-between gap-3 bg-black/40" onClick={(e) => e.stopPropagation()}>
            <div className="text-white">
              <div className="font-sans text-2xl text-[#FFD27A]">{formatINR(series.price)}</div>
              <div className="tracking-luxe text-[10px] text-white/60">per piece</div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => enquireCard(activeCard)}
                data-testid="lightbox-enquire-whatsapp"
                className="inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#1ebe57] text-white px-6 py-3 transition-colors"
              >
                <MessageCircle size={16} />
                <span className="tracking-luxe text-xs">Enquire on WhatsApp</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Individual design tile with scroll-reveal entrance. Mirrors the row treatment on
// CatalogPage so the whole site has a consistent "scroll-in" feel.
function DesignRevealTile({ card: c, series, index: idx, seriesPrice, formatINR, onOpenLightbox, onEnquire }) {
  const ref = useScrollReveal();
  const { isSaved, toggle } = useSavedDesigns();
  const saved = isSaved(c.id);
  // Stagger across rows in the grid (8-12 visible at once on desktop).
  const delay = `${Math.min(idx, 8) * 50}ms`;
  // Prefer the new multi-select array, fall back to legacy singular field.
  const tags = (c.religions && c.religions.length) ? c.religions : (c.religion ? [c.religion] : []);
  return (
    <div
      ref={ref}
      data-testid={`series-card-${c.id}`}
      className="bg-white border border-[#E5E0D8] hover:border-[#F4A100] hover:shadow-lg hover:-translate-y-[2px] transition-all duration-300"
      style={{ transitionDelay: delay }}
    >
      <button
        type="button"
        onClick={onOpenLightbox}
        data-testid={`open-card-lightbox-${c.id}`}
        draggable={false}
        style={{ WebkitTapHighlightColor: "transparent", WebkitTouchCallout: "none" }}
        className="block w-full text-left select-none touch-manipulation cursor-zoom-in"
      >
        <div className="aspect-[4/5] bg-[#F5F2EA] relative sv-img-zoom pointer-events-none overflow-hidden">
          <AuthedImage
            path={c.image_path}
            alt={c.name}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out"
            thumb
            onContextMenu={(e) => e.preventDefault()}
            trackContext={{ cardId: c.id, seriesId: series?.id }}
          />
          <div className="absolute top-3 left-3">
            {c.available ? (
              <span className="bg-[#ECFDF5] text-[#065F46] tracking-luxe text-[10px] px-2.5 py-0.5 rounded-full">In Stock</span>
            ) : (
              <span className="bg-[#FEF2F2] text-[#991B1B] tracking-luxe text-[10px] px-2.5 py-0.5 rounded-full">Sold Out</span>
            )}
          </div>
          {/* Save (heart) toggle — fills maroon when saved, outlined when not. Stops
              the click from bubbling up so it doesn't open the lightbox. */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              toggle({ ...c, series_id: series?.id, series_name: series?.name, price: seriesPrice });
              if (typeof window !== "undefined" && navigator.vibrate) navigator.vibrate(8);
            }}
            data-testid={`save-design-${c.id}`}
            aria-label={saved ? "Remove from saved" : "Save design"}
            className={`absolute top-2.5 right-2.5 w-9 h-9 rounded-full flex items-center justify-center backdrop-blur-sm transition-all pointer-events-auto ${
              saved
                ? "bg-[#D62828]/95 text-white shadow-md scale-100"
                : "bg-white/85 text-[#D62828] hover:bg-white hover:scale-105"
            }`}
          >
            <Heart size={16} fill={saved ? "currentColor" : "none"} strokeWidth={1.75} />
          </button>
        </div>
      </button>
      <div className="p-3 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="font-serif text-base text-[#1A1A1A] truncate">{c.code || `Design ${idx + 1}`}</div>
          <div className="font-sans text-sm text-[#D62828]" data-testid={`design-price-${c.id}`}>{formatINR(seriesPrice)}</div>
          {tags.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1" data-testid={`design-religion-${c.id}`}>
              {tags.map((t) => (
                <span
                  key={t}
                  className="bg-[#F5F2EA] text-[#D62828] tracking-luxe text-[9px] px-2 py-0.5 rounded-full"
                >
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>
        <button
          onClick={onEnquire}
          data-testid={`enquire-card-${c.id}`}
          className="flex-shrink-0 p-2 text-[#25D366] hover:bg-[#25D366]/10 rounded-full transition-colors"
          title="Enquire on WhatsApp"
        >
          <MessageCircle size={18} />
        </button>
      </div>
    </div>
  );
}

